import { File, FileSpreadsheet, FileText, Image as ImageIcon, Mic, Presentation, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { AttachmentRef } from './attachments-api'

interface AttachmentPillProps {
  attachment: AttachmentRef
  onRemove?: () => void
  onClick?: () => void
  className?: string
}

const ICON: Record<AttachmentRef['kind'], React.FC<{ className?: string }>> = {
  image: ImageIcon,
  audio: Mic,
  pdf: FileText,
  office: FileSpreadsheet,
  pptx: Presentation,
  text: File,
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`
  return `${(n / 1024 / 1024).toFixed(1)}MB`
}

export function AttachmentPill({ attachment, onRemove, onClick, className }: AttachmentPillProps) {
  const Icon = ICON[attachment.kind] ?? File
  return (
    <div
      className={cn(
        'inline-flex max-w-[240px] min-w-0 items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2 py-1 text-xs hover:bg-muted min-[761px]:max-w-xs',
        className,
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-h-11 min-w-11 flex-1 items-center gap-1.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:min-h-9 min-[761px]:min-w-9"
      >
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate font-medium" title={attachment.filename}>{attachment.filename}</span>
        <span className="shrink-0 text-muted-foreground">{formatBytes(attachment.size_bytes)}</span>
      </button>
      {onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          aria-label={`Anhang ${attachment.filename} entfernen`}
          className="ml-1 h-11 w-11 shrink-0 p-0 text-muted-foreground hover:text-foreground min-[761px]:h-9 min-[761px]:w-9"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  )
}
